import * as React from 'react'
import { ReactElement, useContext, useEffect, useState } from 'react'
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    makeStyles,
    SearchBox,
    tokens,
} from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'
import useEntity from '../../../hooks/useEntity'
import KvkSearchResultList from '../../List/KvkSearchResult/KvkSearchResultList'
import Loading from '../../Loading'
import { AxiosError } from 'axios'
import { useTranslation } from 'react-i18next'
import useLegalStructures from '../../../hooks/useLegalStructures'
import { Destination } from '../../../context/AxiosContext'
import { LegalPersonMutationDto } from '../../../v2/config/partyMutationConfig'
import {
    KvkBranchProfilePage,
    KvkProfilePage,
    KvkSearchPage,
    KvkSearchResult,
    mapBranchProfileResult,
    mapProfileResult,
    mapSearchResult,
} from '../../../v2/helpers/kvkMappingHelper'
import AuthorizationContext from '../../../context/AuthorizationContext'

export type KvkError = { code: string; omschrijving: string }

export type KvkErrorResponse = {
    fout: KvkError[]
}

export const isKvkError = (error: AxiosError): error is AxiosError<KvkErrorResponse> => !!(error.response?.data as KvkErrorResponse)?.fout

export type KvkSearchDialogProps = {
    hidden?: boolean
    onDismiss?: () => void
    onSelectCompany: (company: Partial<LegalPersonMutationDto>) => void
}

const useStyles = makeStyles({
    surface: {
        maxWidth: '960px',
        width: 'min(960px, 90vw)',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: tokens.spacingVerticalM,
        minHeight: '220px',
    },
})

export default function KvkSearchDialog(props: KvkSearchDialogProps): ReactElement {
    const styles = useStyles()
    const { host: currentHost } = useContext(AuthorizationContext)
    const { t: translate } = useTranslation()
    const { getLegalStructureByName } = useLegalStructures()

    const [input, setInput] = useState<string>('')
    const [query, setQuery] = useState<string>(null)
    const [selected, setSelected] = useState<KvkSearchResult>(null)
    const [items] = useEntity<KvkSearchResult[], KvkSearchPage, KvkErrorResponse>(
        query => ({
            url: `/coc/kvk/search/${currentHost?.RowKey}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock },
            params: { query },
        }),
        query !== '' ? query : null,
        result => mapSearchResult(result.data),
    )
    const [selectedItem] = useEntity<Partial<LegalPersonMutationDto>, KvkProfilePage, KvkErrorResponse>(
        number => ({
            url: `/coc/kvk/profile/${currentHost?.RowKey}/${number}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock },
        }),
        selected?.number ?? null,
        result => mapProfileResult(getLegalStructureByName, result.data),
    )
    const [selectedBranch] = useEntity<Partial<LegalPersonMutationDto>, KvkBranchProfilePage, KvkErrorResponse>(
        branchNumber => ({
            url: `/coc/kvk/branch/${currentHost?.RowKey}/${branchNumber}`,
            baseURL: process.env.API_HOST,
            headers: { Destination: Destination.Hammock },
        }),
        selected?.branchNumber ?? null,
        result => mapBranchProfileResult(result.data),
    )
    const onDismiss = () => {
        setInput('')
        setQuery(null)
        setSelected(null)
        props.onDismiss?.()
    }

    useEffect(() => {
        if (selectedItem.loaded && (!selected?.branchNumber || selectedBranch.loaded)) {
            // De naam van het aangeklikte zoekresultaat wint van de statutaire naam uit het
            // basisprofiel: bij een vestiging is dat de handelsnaam van die vestiging.
            props.onSelectCompany({ ...selectedItem.data, ...(selectedBranch.loaded ? { ...selectedBranch.data, name: selected.name } : {}) })
            onDismiss()
        }
    }, [selectedItem, selectedBranch])

    return (
        <Dialog open={!props.hidden} onOpenChange={(_event, data) => !data.open && onDismiss()}>
            <DialogSurface className={styles.surface}>
                <DialogBody>
                    <DialogTitle
                        action={
                            <Button appearance={'subtle'} aria-label={translate('action.cancel')} icon={<DismissRegular />} onClick={onDismiss} />
                        }
                    >
                        {translate('party.edit.coc.search.title')}
                    </DialogTitle>
                    <DialogContent className={styles.content}>
                        {selectedItem.loading || selectedBranch.loading ? (
                            <Loading />
                        ) : (
                            <>
                                <SearchBox
                                    value={input}
                                    placeholder={translate('party.edit.coc.search.title')}
                                    onChange={(_event, data) => {
                                        setInput(data.value)
                                        if (!data.value) {
                                            setQuery(null)
                                        }
                                    }}
                                    onKeyDown={event => event.key === 'Enter' && setQuery(input)}
                                />
                                <KvkSearchResultList items={items} onSelect={setSelected} />
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button appearance={'secondary'} onClick={onDismiss}>
                            {translate('action.cancel')}
                        </Button>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    )
}
